import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useCallback, useMemo } from 'react';
import { useSurveyFilters } from '@hotjar/feedback/hooks/useSurveyFilters';
import { useCurrentSite } from '@hotjar/site';
import { filtersToSearchClauses } from '@hotjar/feedback/domain/Surveys/Responses/Filters/clauses';
import { type KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useTranslation } from '@hotjar/translations';
import { useSurveyBreakdown, useSurveyOverview } from '@hotjar/feedback/domain/Surveys/query';
import type { Clause } from '@hotjar/feedback/domain/Surveys/Responses/Filters/Clause';
import { SurveyView } from '../SurveyView';
import { useCurrentSurveyId } from '../hooks/useCurrentSurveyId';
import { PerformanceAlerts } from './Alerts/PerformanceAlerts';
import { SurveyBreakdown } from './SurveyBreakdown';
import { SurveyOverview } from './SurveyOverview';
import { isSupportedDateRange } from './utils/dates';

const Wrapper = styled.div`
  padding: ${tokens.spacer.x4} 0;
`;

export const SurveyPerformanceView = (): JSX.Element | null => {
  const { t } = useTranslation('feedback', { keyPrefix: 'surveys.performance' });

  const { data: site } = useCurrentSite();

  const { localFilters, globalFilters } = useSurveyFilters();
  const { surveyId, isSurveyIdLoaded } = useCurrentSurveyId(site?.id);

  const clauses: Clause[] = useMemo(
    () => filtersToSearchClauses(localFilters, globalFilters),
    [localFilters, globalFilters],
  );

  const breakdownResult = useSurveyBreakdown({ siteId: site?.id, surveyId, filters: clauses });
  const overviewResult = useSurveyOverview({ siteId: site?.id, surveyId, filters: clauses });

  const overview = overviewResult.data?.overview ?? null;
  const questions = breakdownResult.data?.breakdown.questions ?? null;

  const isRestricted =
    (breakdownResult.data?.plan_restricted === true || overviewResult.data?.plan_restricted) ??
    false;

  const showUpsellModal = useCallback(
    (referrer: KnownReferrer) => {
      if (site === undefined) return;
      upsell({ site, referrer });
    },
    [site],
  );

  if (surveyId === undefined || site === undefined) return null;

  const getError = (): string | null => {
    const error =
      breakdownResult.error?.response?.data?.error ??
      overviewResult.error?.response?.data?.error ??
      null;

    if (error === null || typeof error === 'string') return error;
    return t('alerts.error-default');
  };

  return (
    <SurveyView view="performance" surveyId={surveyId} isLoaded={isSurveyIdLoaded}>
      <Wrapper>
        <PerformanceAlerts
          isRestricted={isRestricted}
          isSupportedDateRange={isSupportedDateRange(clauses)}
          onUpsell={() => showUpsellModal('surveys.performance')}
          questionCount={questions?.length}
          error={getError()}
        />
        <SurveyOverview
          isRestricted={isRestricted}
          isLoading={overviewResult.isLoading}
          overview={overview}
        />
        <SurveyBreakdown isRestricted={isRestricted} questions={questions} />
      </Wrapper>
    </SurveyView>
  );
};
